<template>
  <div
    id="industries-candidate"
    class="create-profile-step-container"
  >
    <step-title
      :title="title"
      :subtitle="subtitle"
      :icon="icon"
      :help-text="helpText"
    />
    <div class="title is-6">
      <p class="has-text-grey">
        (Starting with most recent)
      </p>
    </div>
    <div
      v-for="(industry, index) in industries"
      :key="industry.key"
      class="card card-content degree-container"
    >
      <form
        novalidate
        @submit.prevent="selectIndustryFromSearchResults({ industry: industriesAutoComplete[index][highlightedIndustryIndex], years_experience: industry.years_experience })"
      >
        <label class="label">Industry</label>
        <div class="field">
          <div
            class="control"
            :class="autoCompleteIsLoading[index] ? 'is-loading' : ''"
          >
            <input
              v-model="industry.industry"
              v-validate="'required'"
              :name="`Industry Name ${index + 1}`"
              class="input"
              type="text"
              placeholder="Ex: Hospitality..."
              @keyup="debouncedIndustrySearch($event, index)"
              @focus="setFocusedIndex(index)"
              @blur="inputIsFocused = false"
              @keyup.up="keyboardUp()"
              @keyup.down="keyboardDown()"
            >
            <p
              v-show="errors.has(`Industry Name ${index + 1}`)"
              class="help is-danger"
            >
              {{ errors.first(`Industry Name ${index + 1}`) }}
            </p>
            <ul
              v-if="industriesAutoComplete[focusedIndex].length > 0"
              class="autocomplete"
            >
              <li
                v-for="(result, resultsIndex) in industriesAutoComplete[index]"
                :key="result.key"
                :class="resultsIndex === highlightedIndustryIndex ? 'is-highlighted' : ''"
                @click="selectIndustryFromSearchResults({ industry: result, years_experience: industry.years_experience })"
                @mouseover="highlightIndustry(resultsIndex)"
              >
                {{ result }}
              </li>
            </ul>
          </div>
        </div>
        <div class="field">
          <div class="control is-expanded">
            <label class="label">Years of Experience</label>
            <input
              v-model="industries[index].years_experience"
              v-validate="'required|min_value:1|max_value:99'"
              class="input is-fullwidth"
              type="number"
              min="1"
              maxlength="2"
              :name="`Industry Experience ${index + 1}`"
              required
              @change="updateIndustries()"
              @keyup="updateIndustries()"
            >
            <p
              v-show="errors.has(`Industry Experience ${index + 1}`)"
              class="help is-danger"
            >
              {{ errors.first(`Industry Experience ${index + 1}`) }}
            </p>
          </div>
        </div>
      </form>
      <div @click="removeIndustry(index)">
        <i class="fas fa-times-circle" />
      </div>
    </div>
    <div
      v-if="isLastIndustryFilled"
      class="add-industry has-text-primary"
      @click="addCurrentIndustry"
    >
      <i class="fas fa-plus" />
      <small
        v-if="industries.length === 0"
        class="has-text-weight-bold"
      >Add An Industry</small>
      <small
        v-else
        class="has-text-weight-bold"
      >Add Another Industry</small>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import StepTitle from './StepTitle';

export default {
  name: 'IndustriesCandidate',
  components: {
    StepTitle
  },
  data () {
    return {
      title: 'Industries',
      subtitle: 'Add industries you have experience with.',
      icon: 'fa-warehouse',
      helpText: '',
      industries: [],
      industriesAutoComplete: [],
      highlightedIndustryIndex: [],
      focusedIndex: 0,
      autoCompleteIsLoading: []
    };
  },
  computed: {
    isLastIndustryFilled () {
      if (this.industries.length > 0) {
        let lastIndex = this.industries.length - 1;
        return (
          this.industries[lastIndex].industry !== ''
          && this.industries[lastIndex].years_experience !== 0
        );
      } else {
        return true;
      }
    },

    isFormValid () {
      return (
        this.industries.length > 0
        && Object.keys(this.fields).every(key => this.fields[key].valid)
      );
    }
  },

  created () {
    this.industries = this.$store.state.user.profile.industries.current;
    this.industriesAutoComplete = this.industries.map(a => []);
    this.autoCompleteIsLoading = this.industries.map(a => false);
  },
  mounted () {
    this.checkForm();
  },
  updated () {
    this.industries = this.$store.state.user.profile.industries.current;
  },
  methods: {
    addCurrentIndustry () {
      this.industries.push({
        industry: '',
        years_experience: 0
      });
      this.industriesAutoComplete.push([]);
      this.autoCompleteIsLoading.push(false);
      this.updateIndustries();
    },

    removeIndustry (index) {
      if (index > -1) {
        this.industries.splice(index, 1);
      }
      this.updateIndustries();
    },

    updateIndustries () {
      this.checkForm();
      this.$store.commit('EDIT_CANDIDATE_INDUSTRIES', this.industries);
    },

    debouncedIndustrySearch: debounce(function ($event) {
      this.searchForIndustries($event);
    }, 200),

    searchForIndustries ($event) {
      this.checkForm();
      /* If user presses UP or DOWN keys, the search will NOT run.
            This prevents running searches when user is trying to navigate
            dropdown options. */
      if (
        $event.code === 'ArrowUp'
        || $event.code === 'ArrowDown'
        || $event.code === 'Enter'
      ) {
        return;
      }

      let query = this.industries[this.focusedIndex].industry;

      if (query.length < 2) {
        return;
      }

      this.industriesAutoComplete[this.focusedIndex] = [];
      this.industriesAutoComplete[this.focusedIndex].push(query);

      this.$set(this.autoCompleteIsLoading, this.focusedIndex, true);
      /* We use the current query as the first result to allow the
            user to select their written option */
      this.$store.dispatch('getIndustries', query)
        .then(response => {
          // Here we concatenate the user's query with all other results.
          let industriesResults = this.industriesAutoComplete[this.focusedIndex].concat(response.data.data.industries); // eslint-disable-line

          if (response.data.data.industries.includes(query)) {
            industriesResults.shift(); // Remove first entry (query) if it is a duplicate.
          }
          this.$set(
            this.industriesAutoComplete,
            this.focusedIndex,
            industriesResults
          );
          this.$set(this.autoCompleteIsLoading, this.focusedIndex, false);
          this.autoCompleteIsLoading[this.focusedIndex] = false;
        })
        .catch(error => console.log(error));
    },

    selectIndustryFromSearchResults (result) {
      this.$set(this.industries, this.focusedIndex, result);
      this.$set(this.industriesAutoComplete, this.focusedIndex, []);
      this.highlightedIndustryIndex = 0;
      this.updateIndustries();
    },

    highlightIndustry (index) {
      this.highlightedIndustryIndex = index;
    },

    keyboardDown () {
      this.highlightedIndustryIndex++;
    },

    keyboardUp () {
      this.highlightedIndustryIndex--;
    },

    setFocusedIndex (index) {
      this.focusedIndex = index;
    },

    checkForm () {
      setTimeout(() => {
        this.$emit('checkForm', this.isFormValid);
      }, 50);
    }
  }
};
</script>

<style lang="scss" scoped>
.industry-column {
  padding-right: 3rem;
}

.fa-plus {
  margin-right: 8px;
}

.add-industry {
  cursor: pointer;
}

.degree-container {
  box-shadow: 0 2px 6px 0 rgba(77, 101, 117, 0.25);
  margin-bottom: 1rem;
}

.card {
  border-radius: 4px;
}

.add-card {
  cursor: pointer;
}

.fa-times-circle {
  position: absolute;
  right: -6px;
  top: -6px;
  cursor: pointer;
}

select:invalid {
  border-color: hsl(0, 0%, 86%);
}

.autocomplete {
  position: absolute;
  z-index: 10;
  background-color: white;
  width: 100%;
  max-height: 150px;
  overflow: scroll;
  border: 1px solid $primary;
  border-top: none;
  margin-top: -1px;
  transition: all 0.2s ease;
}

.is-highlighted {
  background-color: $primary;
  color: white;
  transition: all 0.2s ease;
}

li {
  padding: 0 0.5rem;
  cursor: pointer;
}
</style>
